/**
 * Utilities for simulation calculations
 */

/**
 * Converts annual rate to monthly rate
 * @param annualRate Annual rate in percentage (e.g.: 12.5 for 12.5%)
 * @returns Monthly rate formatted with comma (e.g.: "1,00")
 */
export function convertAnualRateToMonthly(annualRate: number): string {
  const annualRateDecimal = annualRate / 100
  const monthlyRateDecimal = (1 + annualRateDecimal) ** (1 / 12) - 1
  const monthlyRate = monthlyRateDecimal * 100
  return monthlyRate?.toFixed(2)?.replace('.', ',')
}

/**
 * Calculates installments array based on min/max values
 * @param minInstallments Minimum installments value
 * @param maxInstallments Maximum installments value
 * @param steps Steps between installments (default: 12)
 * @returns Array of installments values
 */
export function calculateInstallmentsArray(
  minInstallments: number,
  maxInstallments: number,
  steps: number = 12,
): number[] {
  return Array.from(
    { length: (maxInstallments - minInstallments) / steps + 1 },
    (_, i) => minInstallments + i * steps,
  )
}

/**
 * Calculates default installments value (middle of the array)
 * @param installmentsArray Array of available installments
 * @returns Default installments value
 */
export function getDefaultInstallment(installmentsArray: number[]): number {
  return installmentsArray[Math.floor(installmentsArray.length / 2)]
}

/**
 * Calculates the first due date based on grace period and due day
 * @param defaultFirstDueDate Default first due date from backend (optional)
 * @param isFieldsDisabled Whether fields are disabled (if true and defaultFirstDueDate exists, returns it)
 * @param gracePeriod Grace period in months (number or object with value property)
 * @param dueDay Selected due day (optional)
 * @returns Formatted date string (DD/MM/YYYY) or empty string
 */
export function calculateFirstDueDate(
  defaultFirstDueDate?: string | null,
  isFieldsDisabled?: boolean,
  gracePeriod?: number | { value: number } | null,
  dueDay?: number | null,
): string {
  if (defaultFirstDueDate && isFieldsDisabled) {
    return defaultFirstDueDate
  }

  const gracePeriodMonths = typeof gracePeriod === 'object' && gracePeriod !== null
    ? gracePeriod.value
    : gracePeriod

  if (!gracePeriodMonths || !dueDay) {
    return ''
  }

  const currentDate = new Date()
  const currentDay = currentDate.getDate()
  const extraMonth = dueDay < currentDay ? 1 : 0
  const totalMonths = gracePeriodMonths + extraMonth

  const dueDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + totalMonths, dueDay)

  const day = dueDate.getDate().toString().padStart(2, '0')
  const month = (dueDate.getMonth() + 1).toString().padStart(2, '0')
  const year = dueDate.getFullYear()

  return `${day}/${month}/${year}`
}
